const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

export const keyCovers = (pattern, path) => {
    const p = pattern.split('.');
    const q = path.split('.');
    if (p.length > q.length) return false;
    return p.every((part, i) => part === '*' || part === q[i]);
};

export const collectLeaves = (obj, prefix = '', out = [], depth = 0) => {
    if (depth > 6 || !isObject(obj)) return out;
    Object.entries(obj).forEach(([k, v]) => {
        const path = prefix ? `${prefix}.${k}` : k;
        if (isObject(v)) collectLeaves(v, path, out, depth + 1);
        else out.push({ path, value: v });
    });
    return out;
};

export const readPath = (source, path) => {
    let cur = source;
    for (const part of path.split('.')) {
        if (part === '*' || cur === null || typeof cur !== 'object') return undefined;
        cur = cur[part];
    }
    return cur;
};

export const writePath = (target, path, value) => {
    const parts = path.split('.');
    let cur = target;
    parts.slice(0, -1).forEach((p) => {
        if (!isObject(cur[p])) cur[p] = {};
        cur = cur[p];
    });
    cur[parts[parts.length - 1]] = value;
};

export const parseJson = (text) => {
    try {
        const value = JSON.parse(text);
        return value && typeof value === 'object' ? value : null;
    } catch {
        return null;
    }
};

export const parseObject = (text) => {
    try {
        const value = JSON.parse(text);
        return isObject(value) ? value : null;
    } catch {
        return null;
    }
};

export const diffLeaves = (base, next) => {
    const baseLeaves = new Map(collectLeaves(base).map((l) => [l.path, l.value]));
    const nextLeaves = new Map(collectLeaves(next).map((l) => [l.path, l.value]));
    const changed = {};
    nextLeaves.forEach((value, path) => {
        if (!baseLeaves.has(path) || JSON.stringify(baseLeaves.get(path)) !== JSON.stringify(value)) changed[path] = value;
    });
    const removed = [...baseLeaves.keys()].filter((p) => !nextLeaves.has(p));
    return { changed, removed };
};

export const DEFAULT_HOLD_TOLERANCE_PCT = 2;

export const EMPTY_TARGET = {
    metric: null, direction: null, mode: 'PERCENT', percent: null, value: null, holdTolerancePct: null,
};

const byId = (id) => id;

export const formatTarget = (t, labelOf = byId) => {
    if (!t?.metric || !t.direction) return 'No target set';
    const label = labelOf(t.metric);
    if (t.direction === 'HOLD') return `Hold ${label} (within ±${t.holdTolerancePct ?? DEFAULT_HOLD_TOLERANCE_PCT}%)`;
    const verb = t.direction === 'UP' ? 'Lift' : 'Reduce';
    if (t.mode === 'ABSOLUTE' && t.value != null) return `${verb} ${label} to ${Number(t.value).toLocaleString('en-US')}`;
    if (t.percent != null) return `${verb} ${label} by about ${t.percent}%`;
    return `${verb} ${label}`;
};

// The server's verdict rules, so the playground scores a run the way the real one does. A "reach a level" goal is
// judged by the level reached; the % change only tells a near miss from a miss.
export const targetVerdict = (t, deltaPct, actual = null) => {
    if (!t?.metric) return 'NOT_SCORED';
    const direction = t.direction || 'UP';
    if (direction === 'HOLD') {
        if (deltaPct == null) return 'INCONCLUSIVE';
        return Math.abs(deltaPct) <= (t.holdTolerancePct ?? DEFAULT_HOLD_TOLERANCE_PCT) ? 'HELD' : 'DRIFTED';
    }
    if (t.mode === 'ABSOLUTE') {
        if (actual == null || t.value == null) return 'INCONCLUSIVE';
        if (direction === 'UP' ? actual >= t.value : actual <= t.value) return 'HIT';
        if (deltaPct == null) return 'MISSED';
        return (direction === 'UP' ? deltaPct : -deltaPct) > 0 ? 'PARTIAL' : 'MISSED';
    }
    if (deltaPct == null) return 'INCONCLUSIVE';
    const wanted = direction === 'UP' ? deltaPct : -deltaPct;
    if (wanted <= 0) return 'MISSED';
    return t.percent == null || wanted >= t.percent ? 'HIT' : 'PARTIAL';
};

export const formatGuardrail = (g, labelOf = byId) => {
    const verb = g.direction === 'MUST_NOT_FALL' ? 'must not fall' : 'must not rise';
    const label = labelOf(g.metricKey);
    return g.tolerancePercent != null ? `${label} ${verb} more than ${g.tolerancePercent}%` : `${label} ${verb}`;
};

export const guardrailBroken = (g, pct) => {
    if (pct == null) return false;
    const tol = g.tolerancePercent ?? 0;
    return g.direction === 'MUST_NOT_FALL' ? pct < -tol : pct > tol;
};

export const formatRule = (r) => {
    if (r.text?.trim()) return r.text.trim();
    const k = r.keyPath ?? 'value';
    if (r.op === 'MIN') return `${k} is never below ${r.numValue}`;
    if (r.op === 'MAX') return `${k} is never above ${r.numValue}`;
    if (r.op === 'ONE_OF') return `${k} is one of ${r.strValues.join(', ')}`;
    return `${k} is never empty`;
};

export const violates = (r, value) => {
    const n = Number(value);
    if (r.op === 'MIN') return r.numValue != null && Number.isFinite(n) && n < r.numValue;
    if (r.op === 'MAX') return r.numValue != null && Number.isFinite(n) && n > r.numValue;
    if (r.op === 'ONE_OF') return r.strValues.length > 0 && !r.strValues.includes(String(value));
    return value == null || String(value).trim() === '' || (Array.isArray(value) && value.length === 0);
};

export const coerce = (raw) => {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    if (raw.includes(',')) return raw.split(',').map((s) => s.trim()).filter(Boolean);
    const n = Number(raw);
    if (Number.isFinite(n) && /^-?\d*\.?\d+$/.test(raw)) return n;
    return raw;
};
